import {
    ClickoutLocation,
    OfferPageTemplateResponse,
    SanitisedOffer,
    SrcSet,
} from "@/types/offers";
import { Site } from "@/types/sites";
import { FormEvent, ReactElement } from "react";
import {
    handleOfferClick,
    offerRowClass,
} from "@/components/OfferPage/OfferList";
import { getGAAttributes } from "../../../utils/gaUtils";
import parse, { DOMNode } from "html-react-parser";
import Image, { ImageLoader } from "next/image";
import { debugError } from "@/utils/logs";
import { getImageUrl } from "@/utils/utils";
export const OfferBlockWithForm = ({
    offer,
    useMerchantColours,
    index,
    offerPage,
    site,
    epcId,
    location,
}: {
    offer: SanitisedOffer;
    useMerchantColours?: boolean;
    index: number;
    offerPage: OfferPageTemplateResponse;
    site: Site;
    epcId: string;
    location?: ClickoutLocation;
}): ReactElement => {
    if (!epcId) {
        debugError("Empty epcId for offer:", JSON.stringify(offer));
    }
    const offerHtml = offer.html
        .replaceAll("${index}", index.toString())
        .replace(/<body([^>]*)>/g, '<div$1>')
        .replace(/<\/body>/g, '</div>');
    const formOnSubmitTask = (e: FormEvent<HTMLFormElement>) =>
        handleOfferClick(
            e,
            offer,
            index,
            offerPage,
            site,
            epcId,
            gaAttributes,
            location ?? ClickoutLocation.Offer
        );
    let merchantStyles = {};
    if (useMerchantColours) {
        const playButtonColor = offer?.merchant?.data?.attributes?.colour;
        if (playButtonColor) {
            merchantStyles = {
                "--play-button-color": playButtonColor,
                "--play-button-default-pulse-color": playButtonColor,
                "--play-button-text-color": "#FFFFFF",
                "--play-button-border-color": playButtonColor,
            };
        }
    }

    const createImageElement = ({
        id,
        src,
        srcSet,
        sizes,
        alt,
        width,
        height,
        isPriority,
    }: {
        id?: number;
        src: string;
        srcSet?: SrcSet[];
        sizes?: string;
        alt?: string;
        width?: number;
        height?: number;
        isPriority?: boolean;
    }) => {
        if (!src) {
            return <>src: {src}</>;
        }

        return (
            <Image
                data-media-id={id}
                src={src}
                sizes={sizes}
                alt={alt ?? ""}
                width={width}
                height={height}
                priority={isPriority}
                loading={isPriority ? "eager" : "lazy"}
                fetchPriority={isPriority ? "high" : "auto"}
                quality={100}
                decoding="async"
                style={{
                    objectFit: "contain",
                    maxWidth: "100%",
                    maxHeight: "80%",
                }}
                loader={({ width }) => {
                    if (!srcSet) {
                        return getImageUrl(src);
                    }

                    const correctSize = srcSet?.find(
                        (format) => format.screenWidth === width
                    );
                    if (correctSize) {
                        return correctSize.src + "#?w=" + width;
                    }

                    return src + "#?w=" + width;
                }}
            />
        );
    };

    const transformImage = (node: DOMNode) => {
        if (node.type !== "tag" || node.name !== "img") return null;

        const { src } = node.attribs;
        const isPriority = index < 4;

        const merchantName = offer.merchant.data?.attributes?.name;
        if (src === "#merchantLogo" && offer.logos?.primary) {
            const maxWidth = 275;
            const isTooLarge = offer.logos.primary.width! > maxWidth;
            const aspectRatio =
                (offer.logos.primary.width || 1) /
                (offer.logos.primary.height || 1);
            const newHeight = isTooLarge
                ? Math.ceil(maxWidth / aspectRatio)
                : offer.logos.primary.height!;
            const newWidth = isTooLarge ? maxWidth : offer.logos.primary.width!;

            return createImageElement({
                id: offer.logos.primary.id,
                src: offer.logos.primary.src,
                srcSet: offer.logos.primary.srcSet,
                sizes: offer.logos.primary.sizes,
                alt: `${merchantName} logo`,
                width: newWidth,
                height: newHeight,
                isPriority,
            });
        }

        if (src === "#merchantLogoSecondary") {
            const logo = offer.logos?.secondary ?? offer.logos?.primary;

            if (!logo) {
                return null;
            }

            const maxWidth = 275;
            const isTooLarge = logo.width! > maxWidth;
            const aspectRatio = (logo.width || 1) / (logo.height || 1);
            const newHeight = isTooLarge
                ? Math.ceil(maxWidth / aspectRatio)
                : logo.height!;
            const newWidth = isTooLarge ? maxWidth : logo.width!;

            return createImageElement({
                id: logo?.id,
                src: logo?.src,
                srcSet: logo?.srcSet,
                sizes: logo?.sizes,
                alt: `${merchantName} secondary logo`,
                width: newWidth,
                height: newHeight,
                isPriority,
            });
        }

        return null;
    };

    const transform = (node: DOMNode) => {
        if (node.type === "tag") {
            if (node.name === "img") {
                return transformImage(node);
            }
            if (node.name === "button") {
                const newProps = {
                    ...node.attribs,
                    role: "link",
                    "aria-label": `${offer.merchant.data?.attributes?.name} offer link`,
                };
                node.attribs = newProps;
            }
            if (node.name === "body") {
                return node.children;
            }
        }
        return node;
    };
    const formAttributes = {
        subId: offer?.subId ?? "",
        epcId: offer?.epcId ?? "",
        merchant: offer?.merchant?.data?.attributes?.name ?? "",
    };
    const gaAttributes = getGAAttributes(offer, index);
    if (offer.offerLink) {
        return (
            <div
                className={offerRowClass}
                style={merchantStyles}
                data-ga-attributes={JSON.stringify(gaAttributes)}
                key={offer.id}
            >
                <form
                    onSubmit={(e) => formOnSubmitTask(e)}
                    id={"offer-" + offer.id}
                    className="offer-form"
                    data-attr={JSON.stringify(formAttributes)}
                >
                    {parse(offerHtml, { replace: transform, trim: true })}
                </form>
            </div>
        );
    }

    return <div></div>;
};
